// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { HeaderService } from 'src/app/header.service';
import { of } from 'rxjs';
import { D3eService } from 'src/services/d3e-service';
import {catchError} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
// MatSort is used in html don't remove
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import multiDownload from 'multi-download';
import { SelectedOrgMethod } from 'src/shared/selectedOrg';
import { IHistoryConfig, IThreatCsvToken } from '../../../types/threat';
import { ISensorType } from '../../../types/d3ePolicy';
import {IColumnChooserElement} from '../../shared/components/column-chooser/column-chooser.component';
import {ConfigurationService} from '../../../services/configuration-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

interface Time {
  value: string;
  displayValue: string;
}

interface FilterSelectionType {
  value: string;
  displayValue: string;
}

export interface Threat {
  timestamp: number;
  uuid: 'string';
  deviceName: 'string';
  sensor: 'string';
  sensorDisplay?: 'string';
  responseType: 'string';
  responseTypeDisplay?: 'string';
  enabled: boolean;
  source: 'string';
  activeLockAction?: 'string';
  description?: 'string';
  details?: 'string';
  sourceDisplay?: 'string';
}

const HOUR = 60 * 60 * 1000;
const DAY = 24 * HOUR;
// const WEEK = DAY * 7;

@UntilDestroy({ checkProperties: true })
@Component({
    selector: 'app-history',
    templateUrl: './history.component.html',
    styleUrls: ['./history.component.scss'],
    standalone: false
})
export class HistoryComponent implements OnInit, AfterViewInit {
  selectedOrgId: string;
  showExportThreatsButton = false;

  deviceSetsForm = new UntypedFormControl();
  sensorsForm = new UntypedFormControl();
  enabledForm = new UntypedFormControl();
  sourcesForm = new UntypedFormControl();

  selectedTime = '336';
  gettingThreats = false;

  queryPageStart = 0;
  queryPageSize = 50;
  queryTimeStart = new Date();

  totalResults = null;
  percentLoaded = '0';
  searchValue = null;
  sensorSearchList = [];

  sensorTypes: ISensorType[] = [];
  sensorTypesFiltered: ISensorType[] = [];

  alEngage: string;
  alClear: string;
  alRequest: string;
  alRequestClear: string;
  alRequestEngage: string;
  activeLockTypes: FilterSelectionType[];

  deviceFilterCount = '';
  sensorTypeCount = '';
  activeLockCount = '';

  timestampstart = new Date().getTime();
  timestampend = this.timestampstart - (DAY * 14);

  selectedDevices = [];
  selectedSets = [];
  selectedSensors = [];
  selectedActiveLockTypes = [];
  selectedSources = [];

  deviceSetsEnabled = false;

  deviceSets = [];

  sInternal: string;
  sConsole: string;
  sD3E: string;
  sIntegration: string;
  sourceTypes: FilterSelectionType[];

  tHour: string;
  tHours: string;
  tDay: string;
  tWeek: string;
  tWeeks: string;
  tAll: string;
  times: Time[];

  sortAttrs = [{ 'sortAttr': 'timestamp', 'sortOrder': 'desc' }];

  threatData: Threat[] = [];

  threatDataSource: MatTableDataSource<Threat> = new MatTableDataSource<Threat>();

  columnsToDisplay: string[] = ['timestamp', 'deviceName', 'sensor', 'enabled', 'source', 'description'];
  choosableColumns: IColumnChooserElement[];

  urlTime = null;

  hasUserBeenSet = false;

  licenses = {};

  threatHistoryDataLoaded = false;
  delayedRefreshRequested = false;

  // THREAT HISTORY FIELDS //
  fSearchPlaceholder: string;
  fTimeFilter: string;
  fExportCSV: string;
  fExportJSON: string;
  fEnpointsFilter: string;
  fThreatsFilter: string;
  fActiveLockFilter: string;
  fSourceFilter: string;
  fGroupsFilter: string;
  fThreatsNumber: string;
  fTimestamp: string;
  fEndpoints: string;
  fThreats: string;
  fResponse: string;
  fActiveLock: string;
  fSource: string;
  fDescription: string;
  fDetails: string;
  fNone: string;
  fDescDeception: string;
  fDescAccessed: string;

  title: string;
  sortByProperty = function (property) {
    return function (x, y) {
      return ((x[property] === y[property]) ? 0 : ((x[property] > y[property]) ? 1 : -1));
    };
  };
  sortByOneOfTwoProperties = function (property1, property2) {
    // Sort by the first property if it is set, else sort by the second
    return function (x, y) {
      const x_value = x[property1] ? x[property1] : x[property2];
      const y_value = y[property1] ? y[property1] : y[property2];
      return ((x_value === y_value) ? 0 : ((x_value > y_value) ? 1 : -1));
    };
  };

  constructor(
    private headerService: HeaderService,
    private d3eService: D3eService,
    private changeDetectorRefs: ChangeDetectorRef,
    private route: ActivatedRoute,
    private configurationService: ConfigurationService,
    private translate: TranslateService
  ) {
    headerService.setContextHelp(['help.threat-history']);
    headerService.showRefreshButton(true);
    headerService.refresh$.pipe(untilDestroyed(this)).subscribe(refresh => {
      if (refresh) {
        this.threatData = [];
        this.getThreats();
      }
    })
    this.translate.get(['TH_SEARCH_PLACEHOLDER', 'TH_TIME_LABEL', 'TH_EXPORT_CSV', 'TH_EXPORT_JSON', 'TH_ENDPOINTS_FILTER',
      'TH_THREATS_FILTER', 'TH_ACTIVE_FILTER', 'TH_SOURCE_FILTER', 'TH_GROUPS_FILTER', 'TH_THREATS_NUMBER', 'TH_TIMESTAMP',
      'TH_ENDPOINT', 'TH_THREAT', 'TH_RESPONSE', 'TH_ACTIVE', 'TH_SOURCE', 'TH_DESC', 'TH_DETAILS', 'TH_NONE', 'TH_DESCA',
      'TH_DESCB', 'TITLE_THREAT_HISTORY', 'EVI_INTERNAL', 'EVI_CONSOLE', 'EVI_D3E', 'EVI_INTEGRATION', 'EVI_HOUR', 'EVI_HOURS',
      'EVI_DAY', 'EVI_WEEK', 'EVI_WEEKS', 'EVI_ALL', 'TH_ENGAGE', 'TH_CLEAR', 'TH_REQUEST', 'TH_REQUEST_CLEAR',
      'TH_REQUEST_ENGAGE', 'SELECTOR_ALL_THREATS']).subscribe(vals => {
      this.fDescDeception = vals['TH_DESCA'];
      this.fDescAccessed = vals['TH_DESCB'];
      this.fSearchPlaceholder = vals['TH_SEARCH_PLACEHOLDER'];
      this.fTimeFilter = vals['TH_TIME_LABEL'];
      this.fExportCSV = vals['TH_EXPORT_CSV'];
      this.fExportJSON = vals['TH_EXPORT_JSON'];
      this.fEnpointsFilter = vals['TH_ENDPOINTS_FILTER'];
      this.fThreatsFilter = vals['TH_THREATS_FILTER'];
      this.fActiveLockFilter = vals['TH_ACTIVE_FILTER'];
      this.fSourceFilter = vals['TH_SOURCE_FILTER'];
      this.fGroupsFilter = vals['TH_GROUPS_FILTER'];
      this.fThreatsNumber = vals['TH_THREATS_NUMBER'];
      this.fTimestamp = vals['TH_TIMESTAMP'];
      this.fEndpoints = vals['TH_ENDPOINT'];
      this.fThreats = vals['TH_THREAT'];
      this.fResponse = vals['TH_RESPONSE'];
      this.fActiveLock = vals['TH_ACTIVE'];
      this.fSource = vals['TH_SOURCE'];
      this.fDescription = vals['TH_DESC'];
      this.fDetails = vals['TH_DETAILS'];
      this.fNone = vals['TH_NONE'];

      this.title = vals['TITLE_THREAT_HISTORY'];

      this.choosableColumns = [
        {title: this.fTimestamp, value: 'timestamp', selected: true, hidden: false},
        {title: this.fEndpoints, value: 'deviceName', selected: true, hidden: false},
        {title: this.fThreats, value: 'sensor', selected: true, hidden: false},
        {title: this.fResponse, value: 'responseType', selected: false, hidden: false},
        {title: this.fActiveLock, value: 'enabled', selected: true, hidden: false},
        {title: this.fSource, value: 'source', selected: true, hidden: false},
        {title: this.fDescription, value: 'description', selected: true, hidden: false},
        // {title: this.fDetails, value: 'details', selected: false, hidden: false}
      ];
      this.tHour = vals['EVI_HOUR'];
      this.tHours = vals['EVI_HOURS'];
      this.tDay = vals['EVI_DAY'];
      this.tWeek = vals['EVI_WEEK'];
      this.tWeeks = vals['EVI_WEEKS'];
      this.tAll = vals['EVI_ALL'];
      this.times = [
        { value: '1', displayValue: '1 ' + this.tHour },
        { value: '3', displayValue: '3 ' + this.tHour },
        { value: '24', displayValue: ' 1 ' + this.tDay },
        { value: '168', displayValue: '1 ' + this.tWeek },
        { value: '336', displayValue: '2 ' + this.tWeeks },
        { value: '672', displayValue: '4 ' + this.tWeeks },
        { value: '2016', displayValue: '12 ' + this.tWeeks },
        { value: 'All', displayValue: this.tAll }
      ];
      this.sInternal = vals['EVI_INTERNAL'];
      this.sConsole = vals['EVI_CONSOLE'];
      this.sD3E = vals['EVI_D3E'];
      this.sIntegration = vals['EVI_INTEGRATION'];
      this.sourceTypes = [
        { value: '1', displayValue: this.sD3E },
        { value: '2', displayValue: this.sConsole },
        { value: '3', displayValue: this.sIntegration },
        { value: '4', displayValue: this.sInternal },
      ];
      this.alEngage = vals['TH_ENGAGE'];
      this.alClear = vals['TH_CLEAR'];
      this.alRequest = vals['TH_REQUEST'];
      this.alRequestClear = vals['TH_REQUEST_CLEAR'];
      this.alRequestEngage = vals['TH_REQUEST_ENGAGE'];
      this.activeLockTypes = [
        { value: 'engage', displayValue: this.alEngage },
        { value: 'clear', displayValue: this.alClear },
        { value: 'req_engage', displayValue: this.alRequestEngage },
        { value: 'req_clear', displayValue: this.alRequestClear }
      ];

      this.headerService.setOrgSelectorDefaultOption(vals['SELECTOR_ALL_THREATS']);

    });

    this.route.queryParams.subscribe(params => {
      const timeParam = params['time'];
      if (timeParam !== undefined && timeParam != null) {
        const hourStr = timeParam.split('h')[0];
        this.setTimeInterval(hourStr);
        localStorage.setItem('timeSelection', hourStr + 'h');
      } else {
        const storageTime = localStorage.getItem('timeSelection');
        if (storageTime !== undefined && storageTime != null) {
          const hourStr = storageTime.split('h')[0];
          this.setTimeInterval(hourStr, false);
        }
      }
      const deviceSetParam = params['deviceSetId'];
      if (deviceSetParam !== undefined && deviceSetParam != null) {
        const deviceSetId = parseInt(deviceSetParam, 10);
        this.selectedSets.push(deviceSetId);
        localStorage.setItem('deviceSetSelection', deviceSetParam);
      } else {
        const deviceSetIdSelection = localStorage.getItem('deviceSetSelection');
        if (deviceSetIdSelection !== undefined && deviceSetIdSelection != null) {
          const deviceSetId = parseInt(deviceSetIdSelection, 10);
          this.selectedSets.push(deviceSetId);
        }
      }
      const deviceIdParam = params['deviceId'];
      if (deviceIdParam !== undefined && deviceIdParam != null) {
        const deviceUuids = deviceIdParam.split(',');
        if (deviceUuids.length > 0) {
          for (const deviceUuid of deviceUuids) {
            this.selectedDevices.push(deviceUuid);
          }
        }
      }

      const sensorParam = params['sensorId'];
      if (sensorParam !== undefined && sensorParam != null) {
        const sensors = sensorParam.split(',');
        if (sensors.length > 0) {
          for (const sensor of sensors) {
            const sensorId = parseInt(sensor, 10);
            this.selectedSensors.push(sensorId);
          }
        }
      }
    });
    this.headerService.setTitle(this.title);
    this.headerService.setSubtitle('');
    this.headerService.setIcon('mdi-history');
    this.headerService.setNavMenuText('');
    this.headerService.showUserFilter(true);

    this.headerService.orgSelectedChanged
      .pipe(untilDestroyed(this))
      .subscribe(value => {
        this.selectedOrgId = value;
        if ( this.hasUserBeenSet ) {
          this.selectedSets = [];
          this.queryPageStart = 0;
          this.getConfig();
        }
        this.hasUserBeenSet = true;
    });
  }

  private setTimeInterval(hourStr: any, setActiveFilter: boolean = true) {
    this.selectedTime = hourStr;
    const hours = parseInt(hourStr, 10);
    this.timestampend = this.timestampstart - (HOUR * hours);

    if (setActiveFilter) {
      // cheat here, if we are setting a time, assume it is from the dashboard and 
      // select active lock. this may need to become a param of its own later
      const defaultActiveLock = this.activeLockTypes[0];
      this.selectedActiveLockTypes.push(defaultActiveLock.value);
      const defaultActiveLockList = [];
      defaultActiveLockList.push(defaultActiveLock);
      this.enabledForm.setValue(defaultActiveLockList, { onlySelf: true });
    }
  }

  ngOnInit() {
    this.threatDataSource.data = [];
    this.totalResults = null;
    this.percentLoaded = '25';
    this.getConfig();
  }

  onSearchKeyUp(searchTerm) {
    this.searchValue = searchTerm;
  }

  getConfig() {
    // this.timestampstart = new Date().getTime();
    // this.timestampend = this.timestampstart - (DAY * 14);

    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);

    this.d3eService
      .get_history_config(selectedOrg)
      .pipe(
          catchError(() => of({} as IHistoryConfig)),
          untilDestroyed(this))
      .subscribe(response => {
          this.sensorTypes = response.sensorTypes.sort(this.sortByOneOfTwoProperties('description', 'displayValue'));

          // set the default value for the sensor form based on the sensor ids passed
          const defaultSensorValues = [];
          for (const sensorId of this.selectedSensors) {
            for (const sensorType of this.sensorTypes) {
              if (sensorType.value === sensorId) {
                defaultSensorValues.push(sensorType);
              }
            }
          }
          this.sensorsForm.setValue(defaultSensorValues, { onlySelf: true });

          this.sensorTypesFiltered = this.sensorTypes.filter(s => !s.hidden);
          this.deviceSets = response['deviceSets'];

          if (this.deviceSets.length > 0) {
            this.deviceSetsEnabled = true;
            // set the default value for the device sets form based on the selected sets passed
            if (this.selectedSets.length > 0) {
              const defaultValue = [];
              for (const selectedSet of this.selectedSets) {
                for (const deviceSet of this.deviceSets) {
                  if (deviceSet.id === selectedSet) {
                    defaultValue.push(deviceSet);
                  }
                }
              }
              this.deviceSetsForm.setValue(defaultValue, { onlySelf: true });
            }
          } else {
            this.deviceSetsEnabled = false;
            const defaultValue = [];
            this.deviceSetsForm.setValue(defaultValue, { onlySelf: true });
          }
          this.refreshThreats();

          this.showExportThreatsButton = response.exportThreatsButton;
        },
        error => {
          // TODO: Do something better with the error, like notify the user.
          console.error(`Error retrieving devices: ${error}`);
        }
      );
  }

  refreshThreats() {
    // if a request is currently in process, the data can come back before the request is started.  This puts old data
    // at the top of the list and the new "refresh" data is on the second page.  Here we set delayedRefreshRequested
    // instead of doing a get so that when the existing query returns the refresh will be done in the get's
    // complete() block
    this.delayedRefreshRequested = false;
    if ( this.gettingThreats ) {
      this.delayedRefreshRequested = true;
    } else {
      this.timestampstart = new Date().getTime();
      this.queryPageStart = 0;
      this.threatData = [];
      if (this.sensorTypes.length > 0) {
        this.getThreats();
      }
    }
  }

  buildQuery() {
    const selectedOrg = SelectedOrgMethod.getSelectedOrg(this.selectedOrgId);
    return {
      'pageStart': this.queryPageStart,
      'pageSize': this.queryPageSize,
      'timeHigh': (this.timestampstart / 1000),
      'timeLow': this.timestampend != null ? (this.timestampend / 1000) : null,
      'selectedDevices': this.selectedDevices,
      'selectedSensors': this.selectedSensors,
      'activeLock': this.selectedActiveLockTypes,
      'sources': this.selectedSources,
      'sorts': this.sortAttrs,
      'search': this.searchValue,
      'deviceSets': this.selectedSets,
      'selectedOrgId': selectedOrg,
    };
  }

  getThreats() {
    this.threatHistoryDataLoaded = false;
    this.gettingThreats = true;

    const query = this.buildQuery();
    this.d3eService.get_threats(query)
        .pipe(
            untilDestroyed(this)
        ).subscribe(
        threatResp => {
          // if a refresh is requested, do not process the threats.  throw the response away.  The complete()
          // below will do a refresh
          if ( ! this.delayedRefreshRequested ) {
              const threat_list = threatResp['threats'];
              if (threatResp['totalCount'] != null) {
                this.totalResults = threatResp['totalCount'];
              }
              this.threatData = [...this.threatData, ...this.processThreats(threat_list)];
              this.threatHistoryDataLoaded = true;
            }
          },
          error => {
            // TODO: Do something better with the error, like notify the user.
            console.error(`Error retrieving threats: ${error}`);
          },
          () => {
            this.gettingThreats = false;
            if ( this.delayedRefreshRequested ) {
              this.refreshThreats();
            }

        }
    );
  }

  processThreats(threat_list) {
    const threatList = [];
    threat_list.forEach(threat => {
      let sensorDisplay = threat.sensorType;
      this.sensorTypes.forEach(sensorType => {
        if (sensorType.value === threat.sensorType) {
          sensorDisplay = sensorType.description || sensorType.displayValue;
        }
      });
      threat.sensorDisplay = sensorDisplay;

      let sourceDisplay = '';
      this.sourceTypes.forEach(sourceType => {
        if (parseInt(sourceType.value, 10) === threat.source) {
          sourceDisplay = sourceType.displayValue;
        }
      });
      threat.sourceDisplay = sourceDisplay;

      threat.activeLockAction = this.alClear;
      if (threat.source > 1) {
        // if threat is greater than 1, it is a threat generated by console
        threat.activeLockAction = this.alRequestClear;
        if (threat.enabled != null && threat.enabled) {
          threat.activeLockAction = this.alRequestEngage;
        }
      } else {
        // 1 is D3E, and unset is undetermined, say Engage
        if (threat.enabled != null && threat.enabled) {
          threat.activeLockAction = this.alEngage;
        }
      }

      threat.timestamp = threat.timestamp * 1000;

      threatList.push(threat);
    });
    return threatList;
  }

  ngAfterViewInit() {
    //   Promise.resolve().then(() => {
    //     this.collapses.forEach((collapse: HistoryComponent) => {
    //       collapse.toggle();
    //     });
    //   })
  }

  onTimeChange(event) {
    const newValue = event.value;
    if (newValue === 'All') {
      this.timestampend = null;
      localStorage.removeItem('timeSelection');
    } else {
      const newHours = parseInt(newValue, 10);
      const endTime = new Date(this.timestampstart);
      endTime.setHours(endTime.getHours() - newHours);
      this.timestampend = endTime.getTime();
      localStorage.setItem('timeSelection', newValue + 'h');
    }
    this.refreshThreats();
  }

  onDeviceSetChange(event) {
    this.selectedSets = [];
    localStorage.removeItem('deviceSetSelection');
    if (this.deviceSetsForm.value !== undefined && this.deviceSetsForm.value.length > 0) {
      this.deviceSetsForm.value.forEach(deviceSet => {
        this.selectedSets.push(deviceSet.id);
      });
      if (this.selectedSets.length === 1) {
        localStorage.setItem('deviceSetSelection', this.selectedSets[0].toString());
      }
    }
    this.refreshThreats();
  }

  onSensorsChange(event) {
    this.selectedSensors = [];
    if (this.sensorsForm.value !== undefined && this.sensorsForm.value.length > 0) {
      this.sensorsForm.value.forEach(sensor => {
        this.selectedSensors.push(sensor.value);
      });
    }
    this.refreshThreats();
  }

  onEnabledChange(event) {
    this.selectedActiveLockTypes = [];
    if (this.enabledForm.value !== undefined && this.enabledForm.value.length > 0) {
      this.enabledForm.value.forEach(activeLock => {
        this.selectedActiveLockTypes.push(activeLock.value);
      });
    }
    this.refreshThreats();
  }

  onSourceChange(event) {
    this.selectedSources = [];
    if (this.sourcesForm.value !== undefined && this.sourcesForm.value.length > 0) {
      this.sourcesForm.value.forEach(sourceType => {
        this.selectedSources.push(sourceType.value);
      });
    }
    this.refreshThreats();
  }


  onScrollHandler(event) {
    const tableViewHeight = event.target.offsetHeight; // viewport: ~500px
    const tableScrollHeight = event.target.scrollHeight; // length of all table
    const scrollLocation = event.target.scrollTop; // how far user scrolled

    // If the user has scrolled within 200px of the bottom, add more data
    const buffer = 600;
    const limit = tableScrollHeight - tableViewHeight - buffer;
    if (scrollLocation > limit && this.gettingThreats === false && this.queryPageStart <= this.totalResults) {
      this.queryPageStart += this.queryPageSize;
      this.getThreats();
    }
  }

  sortColumn(event) {
    const sort = {
      'sortAttr': event.active,
      'sortOrder': event.direction
    };

    if (sort['sortOrder'] === '') {
      this.sortAttrs = this.sortAttrs.filter((value) => value['sortAttr'] !== sort['sortAttr']);
      sort['sortOrder'] = 'desc';
    }

    this.sortAttrs = this.sortAttrs.filter((value) => value['sortAttr'] !== sort['sortAttr']);
    this.sortAttrs.push(sort);

    this.refreshThreats();
  }

  exportCSV() {
    const query = this.buildQuery();
    query.pageSize = 0;
    this.d3eService.get_threats_csv_token(query)
      .pipe(
          catchError(() => of({} as IThreatCsvToken)),
          untilDestroyed(this))
      .subscribe(response => {
        const url = this.configurationService.apiUrl + '/history/threats/csv?q=' + response.token;
        multiDownload([url], {
          rename: (url, index, urls) => 'threat-history.csv',
        });
      });
  }

  exportJSON() {
    const query = this.buildQuery();
    query.pageSize = 0;
    this.d3eService.get_threats_csv_token(query)
      .pipe(
          catchError(() => of({} as IThreatCsvToken)),
          untilDestroyed(this))
      .subscribe(response => {
        const url = this.configurationService.apiUrl + '/history/threats/json?q=' + response.token;
        multiDownload([url], {
          rename: (url, index, urls) => 'threat-history.json',
        });
      });
  }

  updateDisplayedColumns(columns: string[]) {
    this.columnsToDisplay = columns;
  }
}
